import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { searchContent } from '@plone/volto/actions/search/search';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import {
  NEWS_PORTAL_TYPES,
  NEWS_PUBLIC_STATES,
} from 'volto-ministerio-publico-federal/constants';

export const MAIS_LIDAS_SUBREQUEST = 'news-mais-lidas';

/** Itens exibidos no card. */
export const MAIS_LIDAS_COUNT = 5;

/**
 * Folga sobre `MAIS_LIDAS_COUNT`: a busca não sabe excluir por caminho, e
 * precisamos descartar a notícia aberta, os destaques da coluna e os cards do
 * "Veja também". Pedimos mais e cortamos depois, para a lista nunca vir curta.
 */
export const MAIS_LIDAS_BUFFER = 8;

const METADATA_FIELDS = [
  'tema',
  'unidadeOrigem',
  'unit_title',
  'effective',
  'description',
];

export interface MaisLidaItem {
  '@id': string;
  title?: string;
  [key: string]: unknown;
}

export interface UseMaisLidasResult {
  items: MaisLidaItem[];
  loading: boolean;
  loaded: boolean;
  /**
   * De onde saiu a ordenação. Hoje sempre `recentes`; quando o serviço de
   * contagem de leituras existir, só esta origem muda — o componente não sabe
   * a diferença.
   */
  source: 'recentes' | 'contagem';
}

interface UseMaisLidasOptions {
  /** Token da unidade de origem da notícia aberta. */
  unidade?: string;
  /** `@id` que não devem aparecer (notícia atual, destaques, "Veja também"). */
  excluded?: Set<string>;
  count?: number;
}

/**
 * Lista do card "Mais lidas".
 *
 * Enquanto não existe contagem real de leituras, a lista são as últimas
 * notícias publicadas pela mesma unidade — o recorte mais próximo do que um
 * ranking de audiência daquela unidade mostraria, e que já é útil ao leitor.
 */
export function useMaisLidas({
  unidade,
  excluded,
  count = MAIS_LIDAS_COUNT,
}: UseMaisLidasOptions): UseMaisLidasResult {
  const dispatch = useDispatch();
  const batchSize = count + MAIS_LIDAS_BUFFER;

  useEffect(() => {
    dispatch(
      searchContent(
        '',
        {
          portal_type: NEWS_PORTAL_TYPES,
          review_state: NEWS_PUBLIC_STATES,
          ...(unidade ? { unidadesPublicacao: unidade } : {}),
          sort_on: 'effective',
          sort_order: 'descending',
          b_size: batchSize,
          metadata_fields: METADATA_FIELDS,
        },
        MAIS_LIDAS_SUBREQUEST,
      ) as any,
    );
  }, [dispatch, unidade, batchSize]);

  const subrequest = useSelector(
    (state: any) => state.search?.subrequests?.[MAIS_LIDAS_SUBREQUEST],
  );

  const rawItems = subrequest?.items as MaisLidaItem[] | undefined;
  // `excluded` é um Set novo a cada mudança de registro; a chave estável
  // abaixo evita refazer o filtro quando o conteúdo não mudou.
  const excludedKey = excluded ? Array.from(excluded).sort().join('|') : '';

  const items = useMemo(() => {
    const list = rawItems ?? [];
    if (!excludedKey) return list.slice(0, count);

    const skip = new Set(
      excludedKey.split('|').map((id) => flattenToAppURL(id)),
    );

    return list
      .filter((item) => !skip.has(flattenToAppURL(item['@id'] || '')))
      .slice(0, count);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawItems, excludedKey, count]);

  return {
    items,
    loading: subrequest?.loading ?? false,
    loaded: subrequest?.loaded ?? false,
    source: 'recentes',
  };
}

export default useMaisLidas;
